import { memo } from 'react';
import {
  SkeletonBlock,
  SkeletonCircle,
  SkeletonLine,
  SkeletonPill,
} from '../common/Skeleton.web';

function HeaderSkeleton() {
  return (
    <header className="cv-loading-header">
      <div className="cv-loading-header-row">
        <SkeletonCircle size={40} />
        <SkeletonLine width={210} height={38} />
      </div>
      <div className="cv-loading-header-subrow">
        <SkeletonLine width="min(320px, 62%)" height={14} />
        <SkeletonPill width={40} height={40} />
      </div>
    </header>
  );
}

function DocumentSkeleton() {
  return (
    <div className="cv-loading-document">
      <HeaderSkeleton />

      <div className="cv-loading-profile-row">
        <SkeletonBlock width={80} height={80} radius={40} />
        <div className="cv-loading-name-block">
          <SkeletonLine width="58%" height={32} />
          <SkeletonLine width="42%" height={15} />
        </div>
      </div>

      <div className="cv-loading-contact-row">
        <SkeletonPill width={118} height={22} />
        <SkeletonPill width={138} height={22} />
        <SkeletonPill width={164} height={22} />
      </div>

      <section className="cv-loading-section">
        <div className="cv-loading-section-title">
          <SkeletonLine width="32%" height={13} />
          <SkeletonBlock height={2} radius={1} />
        </div>
        <SkeletonLine width="100%" height={14} />
        <SkeletonLine width="94%" height={14} />
        <SkeletonLine width="72%" height={14} />
      </section>

      <section className="cv-loading-section">
        <div className="cv-loading-section-title">
          <SkeletonLine width="36%" height={13} />
          <SkeletonBlock height={2} radius={1} />
        </div>
        <SkeletonLine width="88%" height={14} />
        <SkeletonLine width="96%" height={14} />
        <SkeletonLine width="68%" height={14} />
      </section>

      <section className="cv-loading-section">
        <div className="cv-loading-section-title">
          <SkeletonLine width="24%" height={13} />
          <SkeletonBlock height={2} radius={1} />
        </div>
        <div className="cv-loading-pill-row">
          {Array.from({ length: 10 }).map((_, idx) => (
            <SkeletonPill
              key={`cv-skeleton-skill-${idx}`}
              width={52 + (idx % 4) * 18}
              height={25}
            />
          ))}
        </div>
      </section>

      <section className="cv-loading-section">
        <div className="cv-loading-section-title">
          <SkeletonLine width="40%" height={13} />
          <SkeletonBlock height={2} radius={1} />
        </div>
        {Array.from({ length: 2 }).map((_, idx) => (
          <div key={`cv-skeleton-experience-${idx}`} className="cv-loading-entry">
            <div className="cv-loading-entry-top">
              <SkeletonLine width="46%" height={14} />
              <SkeletonLine width={82} height={11} />
            </div>
            <SkeletonLine width="34%" height={12} />
            <SkeletonLine width="90%" height={12} />
          </div>
        ))}
      </section>
    </div>
  );
}

function BottomBarSkeleton() {
  return (
    <div className="cv-loading-bottom-bar" aria-hidden>
      <div className="cv-loading-bottom-bar-inner">
        <SkeletonCircle size={65} />
        <div className="cv-loading-toolbar">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={`cv-skeleton-toolbar-${idx}`} className="cv-loading-toolbar-item">
              <SkeletonCircle size={18} />
              <SkeletonLine width={30} height={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const CVLoadingSkeleton = memo(function CVLoadingSkeleton() {
  return (
    <div className="cv-loading-root" aria-busy="true" aria-live="polite">
      <div className="cv-loading-scroll">
        <DocumentSkeleton />
      </div>
      <div className="cv-loading-fade" aria-hidden />
      <BottomBarSkeleton />
      <style>{`
        .cv-loading-root {
          position: relative;
          display: flex;
          flex-direction: column;
          flex: 1;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: var(--bg);
        }
        .cv-loading-scroll {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding-bottom: 128px;
          box-sizing: border-box;
        }
        .cv-loading-document {
          max-width: 950px;
          width: 100%;
          margin: 0 auto;
          padding: 32px 24px 48px;
          box-sizing: border-box;
          color: var(--text-primary);
          font-family: inherit;
        }
        .cv-loading-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding-bottom: 8px;
          margin-bottom: 16px;
          font-family: 'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif;
        }
        .cv-loading-header-row {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .cv-loading-header-subrow {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          padding-inline-start: 52px;
          min-width: 0;
        }
        .cv-loading-profile-row {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-top: 20px;
        }
        .cv-loading-name-block {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 9px;
        }
        .cv-loading-contact-row,
        .cv-loading-pill-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .cv-loading-contact-row {
          margin-top: 16px;
          gap: 14px;
        }
        .cv-loading-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 28px;
        }
        .cv-loading-section-title {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 4px;
        }
        .cv-loading-entry {
          display: flex;
          flex-direction: column;
          gap: 7px;
          margin-bottom: 14px;
        }
        .cv-loading-entry-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
          min-width: 0;
        }
        .cv-loading-fade {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 128px;
          z-index: 40;
          pointer-events: none;
          background: linear-gradient(to bottom, transparent 0%, var(--bg) 76%);
        }
        .cv-loading-bottom-bar {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 24px;
          z-index: 50;
          display: flex;
          justify-content: center;
          pointer-events: none;
          padding-inline: 16px;
          box-sizing: border-box;
        }
        .cv-loading-bottom-bar-inner {
          width: fit-content;
          max-width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
        }
        .cv-loading-toolbar {
          display: flex;
          align-items: center;
          height: 65px;
          border-radius: 16px;
          padding: 4px 8px;
          background: linear-gradient(to bottom, #F3F3F3 0%, #FFFFFF 60.5%);
          border: 1px solid var(--tabbio-border-subtle);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.12);
          max-width: 100%;
          overflow: hidden;
        }
        .cv-loading-toolbar-item {
          min-width: 40px;
          min-height: 44px;
          padding-inline: 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }
        [data-theme='dark'] .cv-loading-toolbar {
          background: linear-gradient(to bottom, color-mix(in srgb, var(--menu-bg) 92%, #fff 8%) 0%, var(--menu-bg) 100%);
          border-color: var(--tabbio-border-strong);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.28);
        }
        [dir='rtl'] .cv-loading-profile-row,
        [dir='rtl'] .cv-loading-header-row,
        [dir='rtl'] .cv-loading-header-subrow,
        [dir='rtl'] .cv-loading-entry-top,
        [dir='rtl'] .cv-loading-bottom-bar-inner,
        [dir='rtl'] .cv-loading-toolbar {
          flex-direction: row-reverse;
        }
        [dir='rtl'] .cv-loading-header-subrow {
          padding-inline-start: 0;
          padding-inline-end: 52px;
        }
        @media (max-width: 767px) {
          .cv-loading-document {
            padding: 24px 20px 48px;
          }
          .cv-loading-header-row > :first-child {
            display: none;
          }
          .cv-loading-header-subrow {
            padding-inline-start: 0;
          }
          [dir='rtl'] .cv-loading-header-subrow {
            padding-inline-end: 0;
          }
          .cv-loading-profile-row {
            gap: 16px;
          }
          .cv-loading-toolbar-item {
            min-width: 36px;
            padding-inline: 6px;
          }
        }
        @media (max-width: 440px) {
          .cv-loading-bottom-bar {
            padding-inline: 12px;
          }
          .cv-loading-bottom-bar-inner {
            gap: 10px;
          }
          .cv-loading-toolbar {
            overflow-x: auto;
            scrollbar-width: none;
          }
          .cv-loading-toolbar::-webkit-scrollbar {
            display: none;
          }
        }
      `}</style>
    </div>
  );
});
